/**
 * The site's route table — the single place that knows which paths exist.
 *
 * Two readers, which is the whole point of the file: `main.tsx` builds the React router from it,
 * and the Node server (`app/server/`) uses it to tell a real page from an unknown path. Without a
 * shared list the server would have to guess, and guessing is what produced the soft 404s — every
 * unknown path answered 200 with the SPA shell, so Google kept crawling addresses that died with
 * the old site.
 *
 * Plain `.mjs` (typed by the sibling `routes.d.mts`) so the server can import it without a build
 * step: what App Service runs is exactly what is in the repo.
 */

/**
 * CMS-backed pages: every one is rendered by `CmsPage` with the slug below, which is also the
 * name of the JSON the BackOffice publishes for it.
 */
export const CMS_PAGES = [
  // Festival
  { path: '/', slug: 'home' },
  { path: '/billetter', slug: 'billetter' },
  { path: '/program', slug: 'program' },
  { path: '/torsdag', slug: 'torsdag' },
  { path: '/ung-i-midten', slug: 'ung-i-midten' },

  // Praktisk info
  { path: '/nyheder', slug: 'nyheder' },
  { path: '/camping', slug: 'camping' },
  { path: '/praktisk', slug: 'praktisk' },
  { path: '/kontakt', slug: 'kontakt' },
  { path: '/presse', slug: 'presse' },

  // Bliv en del
  { path: '/frivillig', slug: 'frivillig' },

  // Foreningen
  { path: '/om-midtfyns', slug: 'om-midtfyns' },
  { path: '/foreningen', slug: 'foreningen' },
  { path: '/arrangoergruppen', slug: 'arrangoergruppen' },
  { path: '/sekretariatet', slug: 'sekretariatet' },
  { path: '/partnere', slug: 'partnere' },

  // Juridisk
  { path: '/handelsbetingelser', slug: 'handelsbetingelser' },
  { path: '/privatlivspolitik', slug: 'privatlivspolitik' },
  { path: '/vedtaegter', slug: 'vedtaegter' },
];

/**
 * Paths from earlier naming that still exist as routes and move the visitor on. These are part of
 * the app (a client-side <Navigate>), not of the editor's redirect list, because they are our own
 * renamings rather than the old PHP site's addresses.
 */
export const ALIASES = [
  { path: '/historie', to: '/om-midtfyns' },
  { path: '/bagom', to: '/foreningen' },
];

/** Real pages that are deliberately unlisted: reachable by link, kept out of navigation. */
export const INTERNAL_PAGES = [
  { path: '/intern/produktion' },
];

/** A single news story: /nyheder/{id-or-slug}. The list page itself is a CMS page. */
export const NEWS_STORY_PREFIX = '/nyheder/';

/** An editor's unpublished preview of a CMS page: /preview/{guid}. Never indexed. */
export const PREVIEW_PREFIX = '/preview/';

/** Lowercased path, without query/hash, with any trailing slash removed ("/" stays "/"). */
export function normalizePath(pathname) {
  const withoutQuery = String(pathname || '').split(/[?#]/)[0].toLowerCase();
  const trimmed = withoutQuery.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

/** Every path the router answers without a parameter, in one flat list. */
export function staticPaths() {
  return [
    ...CMS_PAGES.map(p => p.path),
    ...ALIASES.map(a => a.path),
    ...INTERNAL_PAGES.map(p => p.path),
  ];
}

/**
 * Does the router have a page for this path?
 *
 * `newsIds` is the set of published story ids and slugs; pass null when it could not be loaded, and
 * every /nyheder/{something} counts as a page. That fallback is deliberate: a CDN hiccup must not
 * turn live articles into 404s — the worst case is the soft 404 we have today, for one path shape.
 */
export function isKnownPath(pathname, newsIds = null) {
  const path = normalizePath(pathname);
  if (staticPaths().includes(path)) return true;

  if (path.startsWith(NEWS_STORY_PREFIX)) {
    const id = path.slice(NEWS_STORY_PREFIX.length);
    if (!id || id.includes('/')) return false;
    return newsIds === null || newsIds.has(id);
  }

  if (path.startsWith(PREVIEW_PREFIX)) {
    const guid = path.slice(PREVIEW_PREFIX.length);
    return !!guid && !guid.includes('/');
  }

  return false;
}
